import { ArrowLeft, CheckCircle2, Clock3, KeyRound, RefreshCw, XCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const KEY_STORAGE = "moselcars-admin-key";

function formatDateTime(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) + " Uhr";
}

function Stat({ label, value, testId }) {
  return <div className="sync-stat" data-testid={testId}><span>{label}</span><strong>{value}</strong></div>;
}

export default function SyncAdminPage({ onBack }) {
  const [adminKey, setAdminKey] = useState(() => window.sessionStorage.getItem(KEY_STORAGE) || "");
  const [keyInput, setKeyInput] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [triggered, setTriggered] = useState(false);

  const load = useCallback(async (key) => {
    if (!key) return;
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/sync-status`, { headers: { "X-Admin-Key": key } });
      if (response.status === 401) {
        window.sessionStorage.removeItem(KEY_STORAGE);
        setAdminKey("");
        setError("Der Admin-Schlüssel ist ungültig.");
        return;
      }
      if (!response.ok) throw new Error("Status konnte nicht geladen werden");
      setData(await response.json());
    } catch (fetchError) {
      setError(fetchError.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(adminKey); }, [adminKey, load]);

  const submitKey = (event) => {
    event.preventDefault();
    const key = keyInput.trim();
    window.sessionStorage.setItem(KEY_STORAGE, key);
    setAdminKey(key);
  };

  const triggerSync = async () => {
    setTriggered(true);
    await fetch(`${BACKEND_URL}/api/admin/sync-now`, { method: "POST", headers: { "X-Admin-Key": adminKey } });
    window.setTimeout(() => { load(adminKey); setTriggered(false); }, 20000);
  };

  const state = data?.state;
  const ok = state?.status === "success";

  return <section className="legal-page page-shell sync-admin" data-testid="sync-admin-page">
    <button className="detail-back-button" type="button" onClick={onBack} data-testid="sync-admin-back-button"><ArrowLeft size={16} /> Zur Startseite</button>
    <div className="legal-header" style={{ position: "static" }}>
      <span className="section-kicker">ADMIN · BESTANDSSYNCHRONISIERUNG</span>
      <h1>Mobile.de <em>Sync.</em></h1>
      <p>Der Fahrzeugbestand wird serverseitig jede volle Stunde mit mobile.de abgeglichen – ohne Browser, ohne Klick. Mobile.de ist die Quelle: neue Anzeigen werden angelegt, geänderte aktualisiert, entfernte deaktiviert (nie gelöscht).</p>
    </div>

    {!adminKey && <form className="sync-key-form" onSubmit={submitKey} data-testid="sync-admin-key-form">
      <label htmlFor="admin-key"><KeyRound size={16} /> Admin-Schlüssel</label>
      <input id="admin-key" type="password" value={keyInput} onChange={(event) => setKeyInput(event.target.value)} placeholder="ADMIN_DASHBOARD_KEY" autoComplete="off" data-testid="sync-admin-key-input" />
      <button className="button button-primary" type="submit" data-testid="sync-admin-key-submit">Anmelden</button>
      {error && <p className="contact-error" role="alert" data-testid="sync-admin-error">{error}</p>}
    </form>}

    {adminKey && data && <>
      <div className={`sync-summary ${ok ? "is-ok" : "is-error"}`} data-testid="sync-summary">
        <div className="sync-summary-head">
          {ok ? <CheckCircle2 size={22} /> : <XCircle size={22} />}
          <div>
            <strong data-testid="sync-status-label">{state.status === "success" ? "Erfolgreich" : state.status === "running" ? "Läuft gerade" : state.status === "error" ? "Fehlgeschlagen – Bestand unverändert" : "Noch kein Lauf"}</strong>
            <span>Letzte Synchronisierung: <b data-testid="sync-last-run">{formatDateTime(state.last_successful_sync_at)}</b> · Nächste: <b data-testid="sync-next-run">{formatDateTime(state.next_run_at)}</b></span>
            <span>Quelle: <b data-testid="sync-provider">{data.seller_api_configured ? "Offizielle mobile.de Seller-API" : "Öffentliche mobile.de-Händlerseite (Seller-API noch nicht konfiguriert)"}</b> · Zeitplan: {data.schedule}</span>
            {state.last_error && <span className="sync-error-text" data-testid="sync-last-error">{state.last_error}</span>}
          </div>
          <button className="button button-secondary" type="button" onClick={triggerSync} disabled={triggered || loading} data-testid="sync-now-button"><RefreshCw size={15} className={triggered ? "is-spinning" : ""} /> {triggered ? "Läuft …" : "Jetzt synchronisieren"}</button>
        </div>
        <div className="sync-stats">
          <Stat label="Fahrzeuge geprüft" value={state.checked} testId="sync-stat-checked" />
          <Stat label="Neue Fahrzeuge" value={state.added} testId="sync-stat-added" />
          <Stat label="Aktualisiert" value={state.updated} testId="sync-stat-updated" />
          <Stat label="Deaktiviert" value={state.deactivated} testId="sync-stat-deactivated" />
          <Stat label="Reaktiviert" value={state.reactivated} testId="sync-stat-reactivated" />
          <Stat label="Aktiv auf der Website" value={data.active_count} testId="sync-stat-active" />
          <Stat label="Fehler" value={state.status === "error" ? 1 : 0} testId="sync-stat-errors" />
        </div>
      </div>

      {!data.seller_api_configured && <div className="sync-hint" data-testid="sync-api-hint">
        <strong>Offizielle Seller-API aktivieren:</strong> Lassen Sie bei mobile.de (service@team.mobile.de) die Seller-API für Ihre Kundennummer freischalten und hinterlegen Sie danach <code>MOBILE_DE_API_USERNAME</code>, <code>MOBILE_DE_API_PASSWORD</code> und <code>MOBILE_DE_SELLER_ID</code> als Secrets. Das System wechselt automatisch auf die API.
      </div>}

      <h2 className="sync-section-title">Protokoll <span>{data.runs.length} Läufe</span></h2>
      <div className="sync-log" data-testid="sync-log">
        {data.runs.length === 0 && <p className="sync-empty">Noch keine Läufe protokolliert.</p>}
        {data.runs.map((run) => <div className={`sync-log-row ${run.status === "error" ? "is-error" : ""}`} key={run.id} data-testid={`sync-run-${run.id}`}>
          <div className="sync-log-time"><Clock3 size={14} /> {formatDateTime(run.started_at)} <small>{run.trigger === "cron" ? "automatisch" : run.trigger}</small></div>
          {run.status === "success"
            ? <div className="sync-log-counts"><span>{run.checked} geprüft</span><span>{run.added} neu</span><span>{run.updated} aktualisiert</span><span>{run.deactivated} deaktiviert</span><span>{run.reactivated} reaktiviert</span><span>{run.errors} Fehler</span></div>
            : <div className="sync-log-counts"><span className="sync-error-text">Fehler: {run.error} – Bestand wurde nicht verändert</span></div>}
        </div>)}
      </div>

      <h2 className="sync-section-title">Deaktivierte Fahrzeuge <span>{data.inactive_vehicles.length}</span></h2>
      <div className="sync-log" data-testid="sync-inactive-list">
        {data.inactive_vehicles.length === 0 && <p className="sync-empty">Aktuell sind alle gespeicherten Fahrzeuge aktiv.</p>}
        {data.inactive_vehicles.map((vehicle) => <div className="sync-log-row" key={vehicle.id} data-testid={`sync-inactive-${vehicle.id}`}><div className="sync-log-time">{vehicle.brand} {vehicle.title} <small>{vehicle.price}</small></div><div className="sync-log-counts"><span>Status: {vehicle.status}</span><span>seit {formatDateTime(vehicle.deactivated_at)}</span><span>mobile.de-ID {vehicle.id}</span></div></div>)}
      </div>
    </>}
    {adminKey && !data && loading && <p className="sync-empty">Lade Synchronisierungsstatus …</p>}
    {adminKey && !data && error && <p className="contact-error" role="alert" data-testid="sync-admin-error">{error}</p>}
  </section>;
}
